
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Campaign } from "@/types/campaigns";
import { CampaignFormData } from "@/components/campaigns/modal/CampaignFormSchema";
import { formatLocalDate } from "@/utils/dateUtils";

export const usePublishExistingCampaign = (userId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (campaignData: Campaign | CampaignFormData): Promise<Campaign> => {
      console.log('Publishing existing campaign:', campaignData);
      
      // Determine status based on start date
      const today = new Date();
      const startDate = 'start_date' in campaignData && campaignData.start_date instanceof Date 
        ? campaignData.start_date 
        : new Date(campaignData.start_date);
      const isStartingToday = startDate.toDateString() === today.toDateString();
      const status = isStartingToday ? 'active' : 'scheduled';
      
      let updateData: any = { 
        status, 
        updated_at: new Date().toISOString() 
      };
      
      // If we receive CampaignFormData (from form submission), update all fields
      if ('name' in campaignData && !('id' in campaignData)) {
        const formData = campaignData as CampaignFormData;
        updateData = {
          ...updateData,
          name: formData.name,
          type: formData.type,
          product: formData.product,
          value: formData.value || 0,
          start_date: formatLocalDate(formData.start_date),
          end_date: formatLocalDate(formData.end_date),
          description: formData.description,
          cashback_template_id: formData.cashback_template_id,
          max_coupons_per_customer: formData.max_coupons_per_customer || 1,
          delay_days: formData.delay_days || 3,
        };
      }
      
      const campaignId = 'id' in campaignData ? campaignData.id : null;
      
      if (!campaignId) {
        throw new Error('Campaign ID is required for publishing existing campaign');
      }
      
      const { data: updatedCampaign, error } = await supabase
        .from('campaigns')
        .update(updateData)
        .eq('id', campaignId)
        .select()
        .single();

      if (error) throw error;

      // If the campaign was activated immediately, apply rules and call webhook directly
      if (status === 'active') {
        const { error: rulesError } = await supabase.rpc('apply_campaign_rules', {
          campaign_id_param: campaignId
        });

        if (rulesError) {
          console.error('Error applying campaign rules:', rulesError);
          // Don't throw here as the campaign was published successfully
        }

        // CHAMAR WEBHOOK DIRETAMENTE - SEM AGUARDAR TRIGGER DO BANCO
        try {
          console.log('🚀 CALLING WEBHOOK DIRECTLY for campaign activation...');
          const { data: webhookData, error: webhookError } = await supabase.functions.invoke('campaign-automation-webhook', {
            body: { 
              auto_process: true, 
              campaign_id: campaignId, 
              event_type: 'campaign.activated',
              direct_call: true  // Flag para indicar chamada direta
            }
          });

          if (webhookError) {
            console.error('❌ Error calling activation webhook directly:', webhookError);
          } else {
            console.log('✅ Activation webhook called directly:', webhookData);
          }
        } catch (error) {
          console.error('❌ Failed to call activation webhook directly:', error);
        }
      }

      return {
        ...updatedCampaign,
        product: updatedCampaign.product as "cashback" | "referral",
        status: updatedCampaign.status as "draft" | "scheduled" | "active" | "completed" | "cancelled"
      } as Campaign;
    },
    onSuccess: (campaign) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', userId] });
      const message = campaign.status === 'active' 
        ? "Campanha publicada e ativada com sucesso" 
        : "Campanha publicada e agendada com sucesso";
      toast.success(message);
    },
    onError: (error) => {
      console.error('Error publishing campaign:', error);
      toast.error("Erro ao publicar campanha");
    },
  });
};
